import { CSSProperties, ReactNode, useCallback, useLayoutEffect, useRef } from "react";
import {
    MotionValue,
    useMotionValue,
    useMotionValueEvent,
    useScroll,
    useSpring,
} from "framer-motion";
import { clamp } from "./morph";
import "./Morph.css";

/**
 * Scrub smoothing. A wheel tick moves the page ~100px in one jump; feeding
 * that straight into a scene makes every morph stutter in steps. The spring
 * lets the scene arrive a few frames late and glide instead.
 */
const SCRUB = { stiffness: 220, damping: 40, mass: 0.5, restDelta: 0.0005 };

/** How long the page has to sit still before the idle loops resume. */
const IDLE_AFTER_MS = 280;

interface SceneTrackProps {
    /** Extra scroll distance, in vh, that the stage stays pinned for. */
    pin: number;
    className?: string;
    /** "r, g, b" — the scene's light colour, for its heartbeat glow. */
    tint?: string;
    children: (p: MotionValue<number>) => ReactNode;
}

/**
 * One scroll scene: a tall track with a viewport-high stage pinned inside it.
 *
 * `p` runs 0 → 1 from the moment the track's top reaches the top of the
 * viewport until its bottom reaches the bottom — i.e. exactly while the stage
 * is pinned. Measured from the track's rect on every scroll (same approach as
 * StorySlot), so a resize or a section above changing height never leaves a
 * stale offset behind.
 *
 * Only mounted in story mode; the pages render their plain layouts otherwise.
 */
export default function SceneTrack({ pin, className = "", tint, children }: SceneTrackProps) {
    const trackRef = useRef<HTMLDivElement>(null);
    const stageRef = useRef<HTMLDivElement>(null);
    const raw = useMotionValue(0);
    const p = useSpring(raw, SCRUB);

    /*
     * The track's document position, cached. Reading its rect on every scroll
     * forced a full layout per frame (scenes have just written styles), six
     * tracks over. The position only changes when something resizes.
     */
    const geo = useRef({ top: 0, span: 1 });

    /*
     * Idle state, as classes on the stage rather than React state: flipping
     * it re-renders nothing. `.is-idle` lets the scene's ambient loops run
     * (they pause in place while scrolling, see Morph.css); `data-mid` marks a
     * scene left half-played, which shows the "keep scrolling" cue.
     */
    const idle = useRef({ scrolling: false, mid: false, timer: 0 });

    const update = useCallback(() => {
        const { top, span } = geo.current;
        const v = clamp((window.scrollY - top) / span);
        raw.set(v);

        const stage = stageRef.current;
        if (!stage) return;
        const st = idle.current;
        const mid = v > 0.01 && v < 0.99;
        if (mid !== st.mid) {
            st.mid = mid;
            stage.toggleAttribute("data-mid", mid);
        }
        if (!st.scrolling) {
            st.scrolling = true;
            stage.classList.remove("is-idle");
        }
        window.clearTimeout(st.timer);
        st.timer = window.setTimeout(() => {
            st.scrolling = false;
            stage.classList.add("is-idle");
        }, IDLE_AFTER_MS);
    }, [raw]);

    const measure = useCallback(() => {
        const track = trackRef.current;
        const stage = stageRef.current;
        if (!track || !stage) return;
        geo.current = {
            top: track.getBoundingClientRect().top + window.scrollY,
            span: Math.max(1, track.offsetHeight - stage.offsetHeight),
        };
        update();
    }, [update]);

    const { scrollY } = useScroll();
    useMotionValueEvent(scrollY, "change", update);

    useLayoutEffect(() => {
        measure();
        // Landing mid-page (hash link, reload) must not play the whole scene
        // in from zero.
        p.jump(raw.get());
        // Body height changes whenever anything above this track resizes.
        const ro = new ResizeObserver(measure);
        ro.observe(document.body);
        window.addEventListener("resize", measure);
        // At rest on arrival, so the loops already run before any scrolling.
        stageRef.current?.classList.add("is-idle");
        const st = idle.current;
        return () => {
            ro.disconnect();
            window.removeEventListener("resize", measure);
            window.clearTimeout(st.timer);
        };
    }, [measure, p, raw]);

    return (
        <div ref={trackRef} className={`scene-track ${className}`} style={{ height: `${100 + pin}vh` }}>
            <div
                ref={stageRef}
                className="scene-stage"
                style={tint ? ({ "--scene-tint": tint } as CSSProperties) : undefined}
            >
                {/* Heartbeat: a glow behind the scene that keeps a pulse
                    going while the reader is still (see Morph.css). */}
                <div className="scene-heart" aria-hidden="true" />
                {children(p)}
                <div className="scene-cue" aria-hidden="true">
                    <span>Scroll</span>
                    <i />
                </div>
            </div>
        </div>
    );
}

/**
 * Re-runs `render` whenever the scene's progress moves, plus once on mount and
 * whenever `deps` change (re-measure after a resize, data arriving, …).
 */
export function useSceneRender(
    p: MotionValue<number>,
    render: (p: number) => void,
    deps: unknown[] = []
) {
    const ref = useRef(render);
    ref.current = render;
    useMotionValueEvent(p, "change", (v) => ref.current(v));
    useLayoutEffect(() => {
        ref.current(p.get());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
}
